import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular/standalone';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

/**
 * HTTP interceptor:
 * 1. Adds Authorization Bearer token
 * 2. Adds x-org-id and x-app-type headers
 * 3. Keeps legacy org_id query param as fallback
 * 4. Handles 401 → token refresh → retry → logout
 */
export const orgIdInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiUrl)) return next(req);

  const auth = inject(AuthService);
  const router = inject(Router);
  const toastCtrl = inject(ToastController);

  const publicPaths = ['/auth/login', '/auth/signup', '/auth/confirm-signup',
    '/auth/forgot-password', '/auth/confirm-forgot-password', '/auth/refresh'];
  const isPublic = publicPaths.some(p => req.url.includes(p));

  let headers = req.headers.set('x-app-type', 'org');

  const orgId = auth.getOrgId();
  if (orgId) {
    headers = headers.set('x-org-id', orgId);
  }

  const tokens = auth.getTokens();
  if (tokens?.accessToken && !isPublic) {
    headers = headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  }

  // Legacy org_id query param fallback
  let url = req.url;
  if (orgId) {
    const parsed = new URL(url);
    if (!parsed.searchParams.has('org_id')) {
      parsed.searchParams.set('org_id', orgId);
    }
    url = parsed.toString();
  }

  const cloned = req.clone({ url, headers });

  return next(cloned).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401 && !isPublic && tokens?.refreshToken) {
        return auth.refreshTokens().pipe(
          switchMap(() => {
            const newTokens = auth.getTokens();
            const retryHeaders = cloned.headers.set(
              'Authorization', `Bearer ${newTokens?.accessToken}`
            );
            return next(cloned.clone({ headers: retryHeaders }));
          }),
          catchError((refreshErr: HttpErrorResponse) => {
            // Only wipe tokens + redirect on a confirmed auth rejection.
            // Network errors (0, 5xx, timeout) should not force re-login —
            // the refresh token is still valid and the user would lose their session
            // just because the server was briefly unreachable.
            if (refreshErr.status === 401 || refreshErr.status === 403) {
              auth.logout();
              // Guard against re-navigating (and resetting the form) if already on /login
              if (!router.url.startsWith('/login')) {
                const message = refreshErr.error?.error || 'Your session has expired. Please log in again.';
                toastCtrl.create({ message, duration: 4000, color: 'warning', position: 'top' })
                  .then(t => t.present());
                router.navigateByUrl('/login');
              }
            }
            return throwError(() => refreshErr);
          })
        );
      }
      return throwError(() => err);
    })
  );
};
